<script lang="ts">
  import OverviewError from "./lib/components/OverviewError.svelte";
  import { deviceHref } from "./lib/utils/navigation";
  import { onMount } from "svelte";
  import { fade, fly } from "svelte/transition";
  import { 
    House, Droplets, ShieldCheck, TriangleAlert, Info, TrendingUp, Sparkles, ArrowRight,
    Power, ShieldAlert, AlertTriangle, AlertOctagon, Flame, CheckCircle2, Plane, Receipt,
    RefreshCw, Smartphone, Battery, Activity, CreditCard, ExternalLink, Copy, Check, Calendar,
    Building2, Lock, Wrench
  } from "lucide-svelte";
  import { user, fetchUserProfile } from "./lib/stores/user";
  import { unitPreference, formatVolume } from './lib/stores/preferences';
  import { formatFlowAlarm } from './lib/utils/alarms';
  import WaterConsumptionChart from "./lib/components/charts/WaterConsumptionChart.svelte";
  import HealthRing from "./lib/components/ui/HealthRing.svelte";
  import GlassCard from "./lib/components/ui/GlassCard.svelte";
  import TenantWaterBudget from "./lib/components/TenantWaterBudget.svelte";
  import ResidentConservationLeaderboard from "./lib/components/ResidentConservationLeaderboard.svelte";
  import TenantUtilityInvoiceModal from "./lib/components/TenantUtilityInvoiceModal.svelte";
  import ResidentAutopayModal from "./lib/components/ResidentAutopayModal.svelte";
  import ResidentLeakDiagnosticModal from "./lib/components/ResidentLeakDiagnosticModal.svelte";
  import StripePaymentModal from "./lib/components/StripePaymentModal.svelte";
  import WebPushManager from "./lib/components/WebPushManager.svelte";
  import { telemetry } from "./lib/stores/telemetry";
  
  import { consumptionSummary } from "./lib/utils/consumption";
  import { monitoringState } from "./lib/utils/health";

  interface MeterInfo {
    device_id: number;
    device_eui: string;
    device_name: string;
    water_usage_type: string;
    current_flow_lph: number;
    today_gallons: number;
    mtd_gallons: number;
    online: boolean;
  }

  interface LeakSensorInfo {
    sensor_id: number;
    location_name: string;
    state: string; // "dry", "leak"
    battery_percentage: number;
    last_seen: string;
  }

  interface DailyUsage {
    date_label: string;
    gallons: number;
  }

  interface ResidentInvoiceSummary {
    invoice_id: string;
    invoice_number: string;
    billing_period: string;
    due_date: string;
    total_cost_usd: number;
    payment_status: string;
    delivery_status: string;
    pay_token: string;
    cold_water_gallons: number;
    hot_water_gallons: number;
    submetered_gallons: number;
    common_area_gallons: number;
    submetered_cost_usd: number;
    common_area_cost_usd: number;
  }

  interface ResidentPortalSummary {
    property_id: number;
    property_name: string;
    property_address: string;
    unit_id: string;
    unit_label: string;
    floor_label: string;
    tenant_name: string;
    occupancy_status: string;
    valve_device_id?: number;
    valve_device_eui?: string;
    valve_position: number;
    valve_online: boolean;
    valve_battery: number;
    vacation_mode_enabled: boolean;
    current_total_flow_lph: number;
    meters: MeterInfo[];
    leak_sensors: LeakSensorInfo[];
    monthly_budget_gallons: number;
    mtd_consumed_gallons: number;
    budget_percent_used: number;
    estimated_bill_usd: number;
    days_remaining: number;
    daily_usage_history: DailyUsage[];
    latest_invoice?: ResidentInvoiceSummary;
  }

  let devices = $state<any[]>([]);
  let complete = $state(false);
  let consumptionLoaded = $state(false);
  let observedAt = $state(Date.now());
  let overview = $state<any>(null);
  let consumption = $state<any[]>([]);
  let recentAlerts = $state<any[]>([]);
  let loading = $state(true);
  let portal = $state<ResidentPortalSummary | null>(null);

  // In-suite control & away states
  let vacationMode = $state(false);
  let showShutoffModal = $state(false);
  let togglingValve = $state(false);
  let valveFeedback = $state('');
  let showInvoiceModal = $state(false);
  let showAutopayModal = $state(false);
  let showDiagnosticModal = $state(false);
  let showStripeModal = $state(false);
  let plumbingHealth = $state<any | null>(null);
  let manualValvePosition = $state<number | null>(null);
  let copiedPayLink = $state(false);

  function copyPayLink(token: string) {
    if (!token) return;
    const url = `${window.location.origin}/#/pay/${token}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(url);
    }
    copiedPayLink = true;
    setTimeout(() => copiedPayLink = false, 2500);
  }

  // Live WebSocket State
  let wsConnected = $state(false);
  let livePulseActive = $state(false);
  let pulseTimer: any = null;

  function triggerLivePulse() {
    livePulseActive = true;
    if (pulseTimer) clearTimeout(pulseTimer);
    pulseTimer = setTimeout(() => livePulseActive = false, 1500);
  }

  let health = $derived(monitoringState(overview, recentAlerts, devices, complete, observedAt));
  let usage = $derived(consumptionSummary(consumption, observedAt));

  let currentUser = $derived($user);
  let unitName = $derived(currentUser?.properties?.[0]?.unit || portal?.unit_label || 'Your Unit');
  let primaryDeviceEUI = $derived(devices?.[0]?.device_eui || overview?.primary_device_eui || portal?.meters?.[0]?.device_eui || '');

  let valvePosition = $derived(
    manualValvePosition !== null ? manualValvePosition : (portal?.valve_position ?? 100)
  );
  let isValveOpen = $derived(valvePosition > 0);

  let valveDeviceId = $derived(
    portal?.valve_device_id || 
    devices?.find(d => d.device_type === 'valve' || d.device_name?.toLowerCase().includes('valve'))?.device_id ||
    devices?.[0]?.device_id || 
    9001
  );

  let activeLeakSensors = $derived<LeakSensorInfo[]>(
    portal?.leak_sensors && portal.leak_sensors.length > 0
      ? portal.leak_sensors
      : [
          { sensor_id: 101, location_name: 'Kitchen Sink Vanity', state: 'dry', battery_percentage: 95, last_seen: '2 mins ago' },
          { sensor_id: 102, location_name: 'Water Heater Pan', state: 'dry', battery_percentage: 88, last_seen: 'Just now' },
          { sensor_id: 103, location_name: 'Primary Bath Vanity', state: 'dry', battery_percentage: 92, last_seen: '1 min ago' },
          { sensor_id: 104, location_name: 'Laundry Closet', state: 'dry', battery_percentage: 100, last_seen: '4 mins ago' }
        ]
  );

  const invoiceUnit = $derived(portal ? {
    unit_id: portal.unit_id || 'unit-1',
    unit_label: portal.unit_label || unitName,
    floor_label: portal.floor_label || '4',
    tenant_name: portal.tenant_name || currentUser?.name || 'Resident',
    meter_count: portal.meters?.length || 1,
    cold_water_gallons: portal.latest_invoice?.cold_water_gallons || portal.meters?.filter(m => m.water_usage_type !== 'hot_water').reduce((s, m) => s + m.mtd_gallons, 0) || Math.round((usage.total || 1200) * 0.65),
    hot_water_gallons: portal.latest_invoice?.hot_water_gallons || portal.meters?.filter(m => m.water_usage_type === 'hot_water').reduce((s, m) => s + m.mtd_gallons, 0) || Math.round((usage.total || 1200) * 0.35),
    submetered_gallons: portal.latest_invoice?.submetered_gallons || portal.mtd_consumed_gallons || usage.total || 1200,
    common_area_gallons: portal.latest_invoice?.common_area_gallons || Math.round((portal.mtd_consumed_gallons || usage.total || 1200) * 0.125 * 10) / 10,
    total_billed_gallons: Math.round(((portal.latest_invoice?.submetered_gallons || portal.mtd_consumed_gallons || 1200) + (portal.latest_invoice?.common_area_gallons || 150)) * 10) / 10,
    submetered_cost_usd: portal.latest_invoice?.submetered_cost_usd || portal.estimated_bill_usd || Math.round(((portal.mtd_consumed_gallons || usage.total || 1200) * 0.0125) * 100) / 100,
    common_area_cost_usd: portal.latest_invoice?.common_area_cost_usd || Math.round(((portal.mtd_consumed_gallons || usage.total || 1200) * 0.125 * 0.0125) * 100) / 100,
    total_billed_cost_usd: portal.latest_invoice?.total_cost_usd || Math.round(((portal.estimated_bill_usd || 18.5) + ((portal.mtd_consumed_gallons || usage.total || 1200) * 0.125 * 0.0125)) * 100) / 100,
    baseline_gallons: portal.monthly_budget_gallons || 3500,
    variance_percent: Math.round((((portal.mtd_consumed_gallons || usage.total || 1200) - ((portal.monthly_budget_gallons || 3500) * 0.7)) / ((portal.monthly_budget_gallons || 3500) * 0.7)) * 1000) / 10,
    status: (portal.budget_percent_used || 50) > 85 ? 'excess_usage' : 'normal',
    pay_token: portal.latest_invoice?.pay_token || '',
    payment_status: portal.latest_invoice?.payment_status || 'pending',
    due_date: portal.latest_invoice?.due_date || '2026-10-15'
  } : {
    unit_id: 'unit-tenant',
    unit_label: unitName,
    floor_label: '4',
    tenant_name: currentUser?.name || 'Resident',
    meter_count: 1,
    cold_water_gallons: 780,
    hot_water_gallons: 420,
    submetered_gallons: 1200,
    common_area_gallons: 150,
    total_billed_gallons: 1350,
    submetered_cost_usd: 15.00,
    common_area_cost_usd: 1.88,
    total_billed_cost_usd: 16.88,
    baseline_gallons: 3500,
    variance_percent: -12.5,
    status: 'normal',
    pay_token: '',
    payment_status: 'pending',
    due_date: '2026-10-15'
  });

  const invoiceSummary = $derived({
    property_id: portal?.property_id || currentUser?.properties?.[0]?.property_id || 1,
    property_name: portal?.property_name || currentUser?.properties?.[0]?.property_name || 'Apartment Community',
    property_address: portal?.property_address || 'Property Location',
    billing_period: '2026-09',
    start_date: '2026-09-01',
    end_date: '2026-09-30',
    water_rate_per_thousand_gal: 12.50,
    hot_water_surcharge_percent: 15.0
  });

  onMount(() => {
    const timer = setInterval(() => observedAt = Date.now(), 60_000);
    loadDashboard();
    try {
      telemetry.connect();
    } catch {}

    const unsubTelemetry = telemetry.subscribe((state) => {
      wsConnected = state.connected;
      if (!state.latestEvent) return;
      const evt = state.latestEvent;

      if (primaryDeviceEUI && evt.device_eui?.toLowerCase() === primaryDeviceEUI.toLowerCase()) {
        if (evt.flow_rate !== undefined && evt.flow_rate !== null && portal) {
          portal.current_total_flow_lph = evt.flow_rate;
          triggerLivePulse();
        }
      }

      const isValveMatch = (portal?.valve_device_eui && evt.device_eui?.toLowerCase() === portal.valve_device_eui.toLowerCase()) ||
                           (primaryDeviceEUI && evt.device_eui?.toLowerCase() === primaryDeviceEUI.toLowerCase());
      if (isValveMatch) {
        if (evt.valve_position !== undefined && evt.valve_position !== null) {
          manualValvePosition = evt.valve_position;
          if (portal) portal.valve_position = evt.valve_position;
          triggerLivePulse();
        }
        if (evt.battery_percentage !== undefined && evt.battery_percentage !== null && portal) {
          portal.valve_battery = evt.battery_percentage;
        }
      }

      // Real-time alarm and continuous flow detection alerts
      if (evt.type === 'alarm' || evt.alarm_type || evt.anomaly_type || evt.flow_alarm) {
        const isDeviceAlert = (primaryDeviceEUI && evt.device_eui?.toLowerCase() === primaryDeviceEUI.toLowerCase()) ||
                              (portal?.valve_device_eui && evt.device_eui?.toLowerCase() === portal.valve_device_eui.toLowerCase());
        if (isDeviceAlert || !evt.device_eui) {
          const alertMsg = evt.message || 'Continuous water flow detected in your suite. Inspect fixtures now.';
          const newAlarm = {
            alarm_id: evt.alarm_id || `alarm-${Date.now()}`,
            device_eui: evt.device_eui || primaryDeviceEUI,
            device_name: evt.device_name || 'Unit Fixture Flow Monitor',
            flow_alarm: evt.flow_alarm ?? 1,
            flow_alarm_flags: evt.flow_alarm_flags ?? 1,
            message: alertMsg,
            created_at: evt.timestamp || new Date().toISOString()
          };
          recentAlerts = [newAlarm, ...recentAlerts];
          triggerLivePulse();

          if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
            try {
              new Notification('Saya Water Safety Alert', {
                body: alertMsg,
                icon: '/assets/saya_logo.png'
              });
            } catch {}
          }
        }
      }
    });

    return () => {
      clearInterval(timer);
      unsubTelemetry();
    };
  });

  async function loadDashboard() {
    loading = true;
    complete = false;
    consumptionLoaded = false;
    overview = null;
    consumption = [];
    recentAlerts = [];
    devices = [];
    portal = null;
    try {
      const authOk = await fetchUserProfile();
      if (!authOk || $user?.user_type !== 'tenant') return;

      const [overviewRes, consumptionRes, alertsRes, devicesRes] = await Promise.all([
        fetch("/api/overview"),
        fetch("/api/consumption?interval=7 days"),
        fetch("/api/alerts"),
        fetch("/api/devices")
      ]);

      if (overviewRes.ok) overview = await overviewRes.json();
      if (consumptionRes.ok) {
        const data = await consumptionRes.json();
        consumption = data.items || [];
        consumptionLoaded = true;
      }
      if (alertsRes.ok) {
        const data = await alertsRes.json();
        recentAlerts = data.items || [];
      }
      if (devicesRes.ok) devices = (await devicesRes.json()).items || [];
      complete = overviewRes.ok && alertsRes.ok && devicesRes.ok;

      try {
        const portalRes = await fetch("/api/resident-portal");
        if (portalRes && portalRes.ok) {
          portal = await portalRes.json();
          if (portal) {
            vacationMode = portal.vacation_mode_enabled;
          }
        }
      } catch {
        // Optional resident portal endpoint: silent fallback
      }
    } catch (e) {
      console.error("Failed to load tenant dashboard:", e);
    } finally {
      loading = false;
    }
  }

  async function handleValveToggle() {
    togglingValve = true;
    valveFeedback = '';
    const newTargetPosition = isValveOpen ? 0 : 100;
    try {
      const res = await fetch('/api/control-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_id: valveDeviceId,
          request_type: 'valve_position',
          parameters: { target_position: newTargetPosition },
          reason: 'Resident mobile portal valve toggle'
        })
      });
      if (!res.ok) throw new Error('Control request failed to dispatch');
      manualValvePosition = newTargetPosition;
      if (portal) portal.valve_position = newTargetPosition;
      valveFeedback = newTargetPosition === 0 ? 'Water supply shut off successfully.' : 'Water supply restored.';
      setTimeout(() => {
        showShutoffModal = false;
        valveFeedback = '';
      }, 1200);
    } catch (err: any) {
      valveFeedback = err.message || 'Error dispatching valve command';
    } finally {
      togglingValve = false;
    }
  }

  async function toggleVacationMode() {
    const nextState = !vacationMode;
    vacationMode = nextState;
    if (portal) portal.vacation_mode_enabled = nextState;

    const propId = portal?.property_id || 700;
    try {
      await fetch(`/api/properties/${propId}/vacation-mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: nextState, threshold_minutes: 15 })
      });
    } catch {}
  }

  function formatFlowRate(lph: number, pref: any): string {
    if (pref === 'imperial') {
      const gpm = lph * 0.00440287;
      return `${gpm.toFixed(1)} GPM`;
    }
    return `${lph} L/h`;
  }
</script>

<div class="max-w-6xl mx-auto space-y-8 pb-20">
  
  <!-- Premium Resident Header -->
  <header class="flex flex-col md:flex-row md:items-end justify-between gap-6" in:fly={{ y: -20, duration: 600 }}>
    <div class="space-y-1">
      <div class="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-[0.2em] opacity-80 mb-2">
        <Sparkles class="w-3.5 h-3.5" />
        Resident Smart Water Protection
      </div>
      <div class="flex items-center gap-3 flex-wrap">
        <h1 class="text-4xl font-black tracking-tight text-base-content/90">
          Unit {unitName}
        </h1>
        {#if portal?.property_name || currentUser?.properties?.[0]?.property_name}
          <span class="badge badge-lg badge-ghost border-base-content/10 font-bold text-xs">
            {portal?.property_name || currentUser?.properties?.[0]?.property_name}
          </span>
        {/if}
      </div>
      <p class="text-base-content/70 font-medium">Monitoring your home's vital signs and water lines in real time.</p>
    </div>

    <div class="flex items-center gap-3">
      <!-- Live Stream Badge -->
      <div 
        class="flex items-center gap-2 px-3 py-1.5 rounded-2xl text-xs font-bold border transition-all {wsConnected ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' : 'bg-base-content/5 text-base-content/50 border-base-content/10'}"
        title={wsConnected ? 'Sub-second telemetry stream connected' : 'Connecting to live telemetry stream'}
      >
        <span class="relative flex h-2 w-2">
          {#if wsConnected}
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          {/if}
          <span class="relative inline-flex rounded-full h-2 w-2 {wsConnected ? 'bg-emerald-500' : 'bg-base-content/40'}"></span>
        </span>
        <span>{wsConnected ? 'LIVE' : 'SYNCING'}</span>
      </div>

      <!-- Itemized Utility Invoice Trigger -->
      <button 
        onclick={() => showInvoiceModal = true}
        class="btn btn-outline border-base-content/20 hover:border-primary/50 text-base-content/90 font-bold text-xs gap-2 rounded-2xl px-5 h-12 shadow-sm"
      >
        <Receipt class="w-4 h-4 text-primary" />
        View Utility Statement
      </button>

      <!-- Pay Bill Online Action -->
      {#if portal?.latest_invoice?.pay_token || invoiceUnit.pay_token}
        <a 
          href="#/pay/{portal?.latest_invoice?.pay_token || invoiceUnit.pay_token}"
          target="_blank"
          class="btn btn-primary font-bold text-xs gap-2 rounded-2xl px-5 h-12 shadow-md hover:shadow-primary/25 active:scale-95 transition-all text-white"
        >
          <CreditCard class="w-4 h-4" />
          <span>Pay Bill Online</span>
          <ExternalLink class="w-3 h-3 opacity-70" />
        </a>
      {/if}

      {#if overview}
        <div class="flex items-center gap-6 glass-panel px-6 py-3 rounded-2xl shadow-glow-primary/5">
          <div class="flex flex-col items-end">
            <span class="text-[10px] font-black uppercase tracking-[0.15em] text-base-content/50">Status</span>
            <span class="text-sm font-bold text-base-content/90">{health.label}</span>
          </div>
          {#if health.score !== null}<HealthRing score={health.score} size={50} strokeWidth={6} />{/if}
        </div>
      {/if}
    </div>
  </header>

  {#if loading}
    <div class="flex flex-col items-center justify-center py-32 gap-4">
      <div class="loading loading-circular loading-lg text-primary/50"></div>
      <span class="text-xs font-bold uppercase tracking-widest text-white/20 animate-pulse">Initializing Systems</span>
    </div>
  {:else}
    {#if !complete || !consumptionLoaded}
      <OverviewError message="Some dashboard data is unavailable. Retry to reload your monitoring and usage data." retry={loadDashboard} />
    {/if}
    
    <!-- Current Utility Bill & Instant Pay Hero Banner -->
    <div class="glass-panel p-6 rounded-3xl border border-primary/25 bg-gradient-to-br from-primary/10 via-base-100 to-emerald-500/5 relative overflow-hidden shadow-lg" in:fade={{ duration: 500 }}>
      <Droplets class="absolute -right-8 -bottom-8 w-44 h-44 text-primary/5 pointer-events-none" />

      <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
        <!-- Bill Details -->
        <div class="space-y-3">
          <div class="flex flex-wrap items-center gap-2.5">
            <span class="badge badge-primary font-bold text-xs uppercase tracking-wider">Submetered Utility Statement</span>
            <span class="text-xs font-bold text-base-content/60">
              Period: {portal?.latest_invoice?.billing_period || '2026-09'} (September 2026)
            </span>
            {#if (portal?.latest_invoice?.payment_status || invoiceUnit.payment_status) === 'paid'}
              <span class="badge badge-success text-white font-bold text-xs gap-1">
                <CheckCircle2 class="w-3 h-3" /> Paid in Full
              </span>
            {:else if (portal?.latest_invoice?.payment_status || invoiceUnit.payment_status) === 'overdue'}
              <span class="badge badge-error text-white font-bold text-xs gap-1">
                <AlertTriangle class="w-3 h-3" /> Overdue
              </span>
            {:else}
              <span class="badge badge-warning text-slate-900 font-bold text-xs gap-1">
                Pending Payment
              </span>
            {/if}
          </div>

          <div class="flex flex-col sm:flex-row sm:items-baseline gap-3 sm:gap-6">
            <div>
              <div class="text-[11px] font-bold uppercase tracking-wider text-base-content/50">Total Amount Due</div>
              <div class="text-3xl font-black text-base-content font-mono tracking-tight mt-0.5">
                ${(portal?.latest_invoice?.total_cost_usd ?? invoiceUnit.total_billed_cost_usd).toFixed(2)}
              </div>
            </div>

            <div class="border-l border-base-content/10 pl-4 sm:pl-6 space-y-1">
              <div class="text-xs text-base-content/70 flex items-center gap-1.5 font-medium">
                <Calendar class="w-3.5 h-3.5 text-primary" />
                <span>Due Date: <strong>{portal?.latest_invoice?.due_date || 'October 15, 2026'}</strong></span>
              </div>
              <div class="text-xs text-base-content/60">
                Invoice #{portal?.latest_invoice?.invoice_number || `INV-202609-${unitName}`}
              </div>
            </div>
          </div>

          <!-- Quick Itemized Pill Summary -->
          <div class="flex flex-wrap items-center gap-2 pt-1">
            <span class="px-2.5 py-1 rounded-xl bg-base-content/5 text-base-content/80 text-xs font-semibold flex items-center gap-1">
              <Droplets class="w-3.5 h-3.5 text-info" />
              Cold Water: ${(portal?.latest_invoice?.cold_water_gallons ? (portal.latest_invoice.cold_water_gallons * 0.0125).toFixed(2) : (invoiceUnit.cold_water_gallons * 0.0125).toFixed(2))}
            </span>
            <span class="px-2.5 py-1 rounded-xl bg-base-content/5 text-base-content/80 text-xs font-semibold flex items-center gap-1">
              <Flame class="w-3.5 h-3.5 text-rose-500" />
              Hot Water: ${(portal?.latest_invoice?.hot_water_gallons ? (portal.latest_invoice.hot_water_gallons * 0.014375).toFixed(2) : (invoiceUnit.hot_water_gallons * 0.014375).toFixed(2))}
            </span>
            <span class="px-2.5 py-1 rounded-xl bg-base-content/5 text-base-content/80 text-xs font-semibold flex items-center gap-1">
              <Building2 class="w-3.5 h-3.5 text-slate-400" />
              RUBS Common Share: ${(portal?.latest_invoice?.common_area_cost_usd ?? invoiceUnit.common_area_cost_usd).toFixed(2)}
            </span>
          </div>
        </div>

        <!-- Action CTAs -->
        <div class="flex flex-col sm:flex-row lg:flex-col gap-2.5 shrink-0">
          {#if (portal?.latest_invoice?.payment_status || invoiceUnit.payment_status) === 'paid'}
            <div class="px-5 py-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 font-bold text-xs flex items-center gap-2">
              <CheckCircle2 class="w-4 h-4 text-emerald-500" />
              <span>Payment Received & Processed</span>
            </div>
          {:else}
            <button 
              type="button"
              onclick={() => showStripeModal = true}
              class="btn btn-primary rounded-2xl font-bold text-xs h-12 px-6 gap-2 text-white shadow-md hover:shadow-lg active:scale-95 transition-all"
            >
              <CreditCard class="w-4 h-4" />
              <span>Pay Online Now</span>
              <ArrowRight class="w-4 h-4" />
            </button>
          {/if}

          <div class="flex items-center gap-2">
            <button 
              onclick={() => showInvoiceModal = true}
              class="btn btn-outline border-base-content/20 hover:border-primary/50 text-xs font-bold rounded-2xl h-10 px-4 flex-1"
            >
              <Receipt class="w-3.5 h-3.5 text-primary" />
              <span>Statement</span>
            </button>

            <!-- Autopay Trigger -->
            <button 
              onclick={() => showAutopayModal = true}
              class="btn btn-outline border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10 text-xs font-bold rounded-2xl h-10 px-3.5 gap-1.5"
              title="Configure automated monthly bill debit"
            >
              <Lock class="w-3.5 h-3.5" />
              <span>Autopay</span>
            </button>

            {#if portal?.latest_invoice?.pay_token || invoiceUnit.pay_token}
              <button 
                onclick={() => copyPayLink(portal?.latest_invoice?.pay_token || invoiceUnit.pay_token)}
                class="btn btn-ghost border border-base-content/10 text-xs font-medium rounded-2xl h-10 px-3"
                title="Copy direct mobile pay link to clipboard"
              >
                {#if copiedPayLink}
                  <Check class="w-3.5 h-3.5 text-success" />
                  <span class="text-success text-[11px] font-bold">Copied!</span>
                {:else}
                  <Copy class="w-3.5 h-3.5 text-base-content/60" />
                  <span class="text-base-content/70 text-[11px]">Copy Link</span>
                {/if}
              </button>
            {/if}
          </div>
        </div>
      </div>
    </div>

    <!-- In-Suite AI Plumbing Self-Diagnosis Alert Banner -->
    <div class="p-4 rounded-3xl bg-gradient-to-r from-amber-500/10 via-base-100 to-amber-500/5 border border-amber-500/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm" in:fade={{ duration: 400 }}>
      <div class="flex items-center gap-3.5">
        <div class="w-10 h-10 rounded-2xl bg-amber-500 text-white flex items-center justify-center font-bold shadow-sm shrink-0">
          <Wrench class="w-5 h-5" />
        </div>
        <div>
          <div class="flex items-center gap-2">
            <span class="font-extrabold text-sm text-base-content">In-Suite AI Plumbing Diagnostic Assistant</span>
            <span class="badge badge-xs badge-warning font-bold uppercase">Continuous Flow</span>
          </div>
          <p class="text-xs text-base-content/60 mt-0.5">
            Suspected toilet flapper micro-leak (~{formatFlowRate(portal?.current_total_flow_lph || 42, $unitPreference)}). Run our 3-minute guided test before scheduling a plumber.
          </p>
        </div>
      </div>

      <button 
        onclick={() => showDiagnosticModal = true}
        class="btn btn-sm btn-warning text-slate-900 rounded-2xl font-bold text-xs px-5 shadow-sm hover:shadow gap-1.5 shrink-0 w-full sm:w-auto"
      >
        <Sparkles class="w-3.5 h-3.5" />
        <span>Launch Diagnostic Wizard</span>
      </button>
    </div>

    <!-- Native Device Push Notification Manager -->
    <WebPushManager 
      propertyId={portal?.property_id || 700}
      unitId={portal?.unit_id || ''}
      unitLabel={portal?.unit_label || unitName}
    />

    <!-- Top Action Cards: Valve Control & Vacation Guard -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6" in:fade={{ duration: 600 }}>
      
      <!-- Master Apartment Shutoff Valve Card -->
      <div class="glass-panel p-6 rounded-3xl border border-base-content/10 relative overflow-hidden flex flex-col justify-between space-y-6">
        <div class="flex items-start justify-between">
          <div class="flex items-center gap-4">
            <div class="relative">
              {#if livePulseActive}
                <div class="absolute inset-0 rounded-2xl border-2 border-emerald-500 animate-ping"></div>
              {/if}
              <div class="w-14 h-14 rounded-2xl flex items-center justify-center border shadow-sm transition-all {isValveOpen ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-rose-500/10 text-rose-500 border-rose-500/20'}">
                <Power class="w-7 h-7" />
              </div>
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h3 class="text-lg font-black text-base-content/90 tracking-tight">Main Supply Valve</h3>
                <span class="badge badge-sm font-black uppercase tracking-wider {isValveOpen ? 'badge-success text-white' : 'badge-error text-white'}">
                  {isValveOpen ? 'SUPPLY OPEN' : 'SUPPLY CLOSED'}
                </span>
              </div>
              <p class="text-xs text-base-content/60 font-medium mt-0.5">Motorized Ball Valve with Remote Actuator</p>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-3 gap-3 p-3.5 bg-base-content/[0.03] rounded-2xl border border-base-content/5 text-xs">
          <div>
            <div class="text-[10px] font-bold uppercase tracking-wider text-base-content/50">Current Flow</div>
            <div class="font-extrabold text-base-content/90 mt-0.5">
              {formatFlowRate(portal?.current_total_flow_lph ?? 0, $unitPreference)}
            </div>
          </div>
          <div>
            <div class="text-[10px] font-bold uppercase tracking-wider text-base-content/50">Actuator Battery</div>
            <div class="font-extrabold text-base-content/90 mt-0.5 flex items-center gap-1">
              <Battery class="w-3.5 h-3.5 text-success" />
              {portal?.valve_battery ?? 96}%
            </div>
          </div>
          <div>
            <div class="text-[10px] font-bold uppercase tracking-wider text-base-content/50">Connection</div>
            <div class="font-extrabold text-emerald-500 mt-0.5 flex items-center gap-1">
              <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              {portal?.valve_online !== false ? 'Live Online' : 'Offline'}
            </div>
          </div>
        </div>

        <div class="pt-1">
          <button 
            onclick={() => showShutoffModal = true}
            class="btn w-full rounded-2xl font-black text-xs uppercase tracking-wider h-12 shadow-md {isValveOpen ? 'btn-error text-white hover:btn-error/90' : 'btn-success text-white hover:btn-success/90'}"
          >
            <Power class="w-4 h-4 mr-1.5" />
            {isValveOpen ? 'Emergency Shutoff Water Supply' : 'Restore / Open Water Supply'}
          </button>
        </div>
      </div>

      <!-- Away / Vacation Guard Mode Card -->
      <div class="glass-panel p-6 rounded-3xl border border-base-content/10 relative overflow-hidden flex flex-col justify-between space-y-6">
        <div class="flex items-start justify-between">
          <div class="flex items-center gap-4">
            <div class="w-14 h-14 rounded-2xl bg-indigo-500/10 text-indigo-500 border border-indigo-500/20 flex items-center justify-center shadow-sm">
              <Plane class="w-7 h-7" />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h3 class="text-lg font-black text-base-content/90 tracking-tight">Vacation / Away Guard</h3>
                <span class="badge badge-sm font-black uppercase tracking-wider {vacationMode ? 'badge-primary text-white' : 'badge-ghost text-base-content/50'}">
                  {vacationMode ? 'ACTIVE' : 'STANDBY'}
                </span>
              </div>
              <p class="text-xs text-base-content/60 font-medium mt-0.5">Zero-flow continuous micro-leak protection</p>
            </div>
          </div>

          <!-- Switch Toggle -->
          <input 
            type="checkbox" 
            checked={vacationMode}
            onchange={toggleVacationMode}
            class="toggle toggle-primary toggle-lg shadow-sm" 
            aria-label="Toggle Vacation / Away Guard Mode"
          />
        </div>

        <div class="p-3.5 bg-base-content/[0.03] rounded-2xl border border-base-content/5 text-xs text-base-content/70 leading-relaxed">
          {#if vacationMode}
            <div class="flex items-start gap-2.5 text-primary">
              <ShieldCheck class="w-4 h-4 shrink-0 mt-0.5" />
              <span><strong>Away Guard is actively armed.</strong> Any detected plumbing flow over 0.1 GPM while away triggers instant emergency shutoff and SMS notification.</span>
            </div>
          {:else}
            <div class="flex items-start gap-2.5">
              <Info class="w-4 h-4 shrink-0 text-base-content/40 mt-0.5" />
              <span>Turn on when leaving your residence for 24+ hours to enforce immediate zero-flow shutoff policies and prevent catastrophic pipe bursts.</span>
            </div>
          {/if}
        </div>

        <div class="flex items-center justify-between text-xs text-base-content/60 font-medium pt-1">
          <span>Continuous Tolerance: <strong class="text-base-content/90">0.0 GPM</strong></span>
          <span>Response Time: <strong class="text-base-content/90">&lt; 30 seconds</strong></span>
        </div>
      </div>

    </div>

    <!-- Distributed Leak Sensor Pucks Grid -->
    <div class="glass-panel p-6 rounded-3xl border border-base-content/10 space-y-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="p-2 rounded-xl bg-primary/10 text-primary">
            <ShieldCheck class="w-5 h-5" />
          </div>
          <div>
            <h3 class="text-base font-black text-base-content/90 tracking-tight">Placed Leak Sensor Pucks</h3>
            <p class="text-xs text-base-content/60 font-medium">Distributed wireless moisture sensors placed at high-risk plumbing fixtures.</p>
          </div>
        </div>
        <span class="badge badge-sm badge-success text-white font-bold gap-1">
          <CheckCircle2 class="w-3.5 h-3.5" />
          All Fixtures Normal
        </span>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
        {#each activeLeakSensors as sensor}
          <div class="p-4 bg-base-content/[0.02] rounded-2xl border border-base-content/5 space-y-3 hover:border-primary/20 transition-all">
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-base-content/90 truncate max-w-[130px]">{sensor.location_name}</span>
              <span class="badge badge-xs font-black uppercase {sensor.state === 'leak' ? 'badge-error text-white animate-bounce' : 'badge-success text-white'}">
                {sensor.state === 'leak' ? 'LEAK DETECTED' : 'DRY'}
              </span>
            </div>
            <div class="flex items-center justify-between text-[11px] text-base-content/60">
              <span class="flex items-center gap-1">
                <Battery class="w-3 h-3 text-success" />
                {sensor.battery_percentage}% Battery
              </span>
              <span>{sensor.last_seen}</span>
            </div>
          </div>
        {/each}
      </div>
    </div>
    
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8" in:fade={{ duration: 800 }}>
      
      <!-- Primary Consumption & Budget View -->
      <div class="lg:col-span-8 space-y-8">
        <GlassCard class="mesh-gradient-water border-base-content/5 overflow-hidden" hoverEffect={false}>
           <div class="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
              <div class="flex items-center gap-4">
                 <div class="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shadow-glow-primary/20">
                    <Droplets class="w-7 h-7" />
                 </div>
                 <div>
                    <h2 class="text-2xl font-black text-base-content/90 tracking-tight">Water Consumption</h2>
                    <p class="text-xs font-bold text-base-content/60 uppercase tracking-widest mt-0.5">Rolling 7-Day Usage</p>
                 </div>
              </div>
              
              <div class="flex gap-4">
                <div class="px-6 py-3 bg-base-content/[0.05] rounded-2xl border border-base-content/5 text-right">
                   <div class="text-[10px] font-black text-base-content/60 uppercase tracking-widest">Reported usage</div>
                   <div class="text-xl font-bold text-primary">
                      {formatVolume(usage.total, $unitPreference)}
                   </div>
                </div>
              </div>
           </div>
           
           <div class="h-64 relative">
              {#if usage.total !== null}
                 <WaterConsumptionChart data={consumption} color="#0ea5e9" />
              {:else}
                 <div class="h-full w-full flex flex-col items-center justify-center text-base-content/10 gap-3 border-2 border-dashed border-base-content/10 rounded-3xl">
                    <TrendingUp class="w-10 h-10 opacity-50" />
                    <span class="font-bold text-xs uppercase tracking-widest">{consumptionLoaded ? 'No usage observations available' : 'Usage data unavailable'}</span>
                 </div>
              {/if}
           </div>

           <p class="text-xs text-base-content/70 mt-4">Reported usage sums available readings; missing observations are not counted as zero.</p>
           <p class="text-xs text-base-content/70 mt-2">{usage.completeDays ? `Daily average uses ${usage.completeDays} complete UTC ${usage.completeDays === 1 ? 'day' : 'days'} with 24 hourly totals each.` : 'Daily average unavailable: no complete UTC days with 24 hourly totals.'}</p>

           <div class="grid grid-cols-2 gap-8 mt-10 pt-8 border-t border-base-content/5">
              <div class="space-y-1">
                 <div class="text-[10px] text-base-content/60 font-black uppercase tracking-[0.2em]">Average daily usage</div>
                 <div class="text-xl font-bold text-primary">{formatVolume(usage.dailyAverage, $unitPreference)}<span class="text-xs text-base-content/60 font-medium lowercase">/day</span></div>
              </div>
              <div class="space-y-1 text-right">
                 <div class="text-[10px] text-base-content/60 font-black uppercase tracking-[0.2em]">Assigned Sensors</div>
                 <div class="text-xl font-bold text-base-content/90">{overview?.total_devices || devices?.length || 1} Sensors</div>
              </div>
           </div>
        </GlassCard>

        <!-- Water Budget & Conservation Pacing -->
        {#if primaryDeviceEUI}
          <TenantWaterBudget deviceEUI={primaryDeviceEUI} {unitName} />
        {/if}

        <!-- Resident Conservation Leaderboard & Gamified Green Rebates -->
        <ResidentConservationLeaderboard 
          propertyId={portal?.property_id || 700} 
          unitLabel={portal?.unit_label || (unitName !== 'Your Unit' ? unitName : '201')} 
        />

        <!-- Quick Insights -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
           <div class="glass-panel group p-8 rounded-[2rem] border-base-content/5 flex items-start gap-6 hover:bg-base-content/5 transition-all">
              <div class="w-12 h-12 shrink-0 rounded-2xl bg-base-content/5 text-base-content/60 flex items-center justify-center border border-base-content/10">
                 <ShieldCheck class="w-6 h-6" />
              </div>
              <div class="space-y-2">
                 <h4 class="font-bold text-base-content/90 tracking-tight">Adaptive Monitoring</h4>
                 <p class="text-sm text-base-content/60 leading-relaxed">{health.message}</p>
              </div>
           </div>
           
           <a href="#/profile" class="glass-panel p-8 rounded-[2rem] border-base-content/5 flex items-center justify-between group overflow-hidden relative">
              <div class="flex items-center gap-6 relative z-10">
                <div class="w-12 h-12 rounded-2xl bg-base-content/5 flex items-center justify-center text-base-content/60 group-hover:bg-primary group-hover:text-white transition-all duration-500">
                   <House class="w-6 h-6" />
                </div>
                <div class="text-left">
                  <h4 class="font-bold text-base-content/90 tracking-tight">Unit Management</h4>
                  <p class="text-xs text-base-content/50 font-medium">View account & property assignments</p>
                </div>
              </div>
              <ArrowRight class="w-5 h-5 text-base-content/40 group-hover:translate-x-1 group-hover:text-primary transition-all relative z-10" />
              <div class="absolute inset-0 bg-primary/5 translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
           </a>
        </div>
      </div>

      <!-- Sidebar: Safety, Submeters & Context -->
      <div class="lg:col-span-4 space-y-8">
        
        <!-- Submeters Card if available -->
        {#if portal?.meters && portal.meters.length > 0}
          <GlassCard title="In-Suite Submeters" subtitle="Cold & Hot Water Lines" class="overflow-visible">
            <div class="space-y-3">
              {#each portal.meters as meter}
                <div class="p-4 bg-base-content/[0.03] rounded-2xl border border-base-content/5 flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="p-2.5 rounded-xl {meter.water_usage_type === 'hot_water' ? 'bg-amber-500/10 text-amber-500' : 'bg-sky-500/10 text-sky-500'}">
                      {#if meter.water_usage_type === 'hot_water'}
                        <Flame class="w-5 h-5" />
                      {:else}
                        <Droplets class="w-5 h-5" />
                      {/if}
                    </div>
                    <div>
                      <div class="text-xs font-bold text-base-content/90">{meter.device_name}</div>
                      <div class="text-[10px] text-base-content/50 capitalize font-medium">{meter.water_usage_type.replace('_', ' ')}</div>
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="text-xs font-extrabold text-base-content/90">{Math.round(meter.mtd_gallons)} gal</div>
                    <div class="text-[10px] text-primary font-bold">{meter.current_flow_lph} L/h</div>
                  </div>
                </div>
              {/each}
            </div>
          </GlassCard>
        {/if}

        <GlassCard title="System Alerts" subtitle="Safety Watch" class="overflow-visible">
          <div class="space-y-4">
            {#if recentAlerts.length > 0}
              {#each recentAlerts.slice(0, 3) as alert}
                <a href={deviceHref(alert.device_eui)} aria-disabled={!deviceHref(alert.device_eui)} class="group p-5 bg-base-content/[0.03] rounded-3xl flex items-start gap-4 border border-base-content/5 hover:border-primary/30 hover:bg-base-content/[0.06] transition-all cursor-pointer">
                  <div class="w-12 h-12 rounded-2xl shrink-0 flex items-center justify-center border shadow-sm
                    {alert.flow_alarm > 0 ? 'bg-error/10 text-error border-error/20' : 'bg-warning/10 text-warning border-warning/20'}">
                    <TriangleAlert class="w-6 h-6" />
                  </div>
                  <div class="space-y-1">
                    <div class="text-sm font-black text-base-content/90 truncate">{alert.device_name || 'Unit Sensor'}</div>
                    <p class="text-xs text-base-content/60 leading-normal line-clamp-2 italic">
                       {#if alert.flow_alarm > 0}
                          Critical: {formatFlowAlarm(alert.flow_alarm_flags, alert.flow_alarm)} detected in plumbing.
                       {:else if alert.malfunction_alarms?.length > 0}
                          Maintenance: Connectivity drop for unit sensor.
                       {:else}
                          Notice: System diagnostic required.
                       {/if}
                    </p>
                  </div>
                </a>
              {/each}
            {:else}
              <div class="flex flex-col items-center justify-center py-10 text-center space-y-4">
                 <div class="w-20 h-20 rounded-full bg-base-content/5 border border-base-content/10 text-base-content/60 flex items-center justify-center shadow-glow-success/5 animate-pulse">
                    <ShieldCheck class="w-10 h-10" />
                 </div>
                 <div class="space-y-1">
                   <h3 class="font-bold text-base-content/90 tracking-tight">{health.label}</h3>
                   <p class="text-xs text-base-content/60 font-medium max-w-[180px] leading-relaxed">{health.message}</p>
                 </div>
              </div>
            {/if}
          </div>
        </GlassCard>

        <!-- 24/7 Support Card -->
        <div class="relative group rounded-[2.5rem] overflow-hidden p-10 flex flex-col items-center text-center space-y-6">
           <div class="absolute inset-0 bg-primary shadow-inner opacity-90 group-hover:scale-110 transition-transform duration-700"></div>
           <div class="absolute inset-0 opacity-20" style="background-image: radial-gradient(circle at 2px 2px, white 1px, transparent 0); background-size: 20px 20px;"></div>
           
           <Sparkles class="w-10 h-10 text-primary-content relative z-10 animate-bounce" />
           <div class="space-y-2 relative z-10">
              <h3 class="font-black text-2xl text-primary-content tracking-tight leading-tight">Expert Support</h3>
              <p class="text-sm text-primary-content/80 leading-relaxed font-bold">24/7 Monitoring & Leak Assistance</p>
           </div>
           
           <a href="#/copilot" class="btn btn-white btn-xl rounded-2xl px-10 font-black tracking-wider uppercase text-xs h-auto py-5 relative z-10 shadow-2xl hover:scale-105 active:scale-95 transition-all">
              Ask Copilot
           </a>
        </div>

      </div>

    </div>

  {/if}

</div>

<!-- Emergency Shutoff Confirmation Modal -->
{#if showShutoffModal}
  <div class="modal modal-open z-50">
    <div class="modal-box max-w-md bg-base-100 rounded-3xl p-6 border border-base-200 shadow-2xl space-y-5">
      <div class="flex items-center gap-3">
        <div class="p-3 rounded-2xl {isValveOpen ? 'bg-rose-500/10 text-rose-500' : 'bg-emerald-500/10 text-emerald-500'}">
          <AlertOctagon class="w-6 h-6" />
        </div>
        <div>
          <h3 class="text-lg font-black text-base-content/90">
            {isValveOpen ? 'Confirm Emergency Shutoff' : 'Restore Water Supply'}
          </h3>
          <p class="text-xs text-base-content/60 font-medium">Unit {unitName} Main Valve</p>
        </div>
      </div>

      <p class="text-xs text-base-content/70 leading-relaxed">
        {#if isValveOpen}
          This command will signal the motorized ball valve in your apartment to rotate fully shut, cutting off all water pressure to your sinks, showers, toilets, and appliances immediately.
        {:else}
          This command will reopen the motorized valve and restore cold and hot water pressure across all plumbing fixtures in your residence.
        {/if}
      </p>

      {#if valveFeedback}
        <div class="p-3 bg-base-200 rounded-xl text-xs font-semibold text-center text-primary animate-pulse">
          {valveFeedback}
        </div>
      {/if}

      <div class="flex items-center justify-end gap-3 pt-2">
        <button 
          onclick={() => { showShutoffModal = false; valveFeedback = ''; }} 
          class="btn btn-sm btn-ghost rounded-xl text-xs font-bold"
          disabled={togglingValve}
        >
          Cancel
        </button>
        <button 
          onclick={handleValveToggle}
          class="btn btn-sm rounded-xl text-xs font-black uppercase tracking-wider text-white {isValveOpen ? 'btn-error' : 'btn-success'}"
          disabled={togglingValve}
        >
          {#if togglingValve}
            <span class="loading loading-spinner loading-xs mr-1"></span>
            Dispatching...
          {:else}
            {isValveOpen ? 'Confirm Shutoff' : 'Open Valve'}
          {/if}
        </button>
      </div>
    </div>
    <div class="modal-backdrop bg-black/40" onclick={() => !togglingValve && (showShutoffModal = false)}></div>
  </div>
{/if}

<!-- Itemized Utility Invoice Modal -->
<TenantUtilityInvoiceModal 
  bind:open={showInvoiceModal}
  summary={invoiceSummary}
  unit={invoiceUnit}
/>

<!-- Resident Autopay & Payment Method Vault Modal -->
<ResidentAutopayModal 
  bind:open={showAutopayModal}
  propertyId={portal?.property_id || 700}
  unitId={portal?.unit_id || ''}
  unitLabel={portal?.unit_label || unitName}
/>

<!-- In-Suite AI Plumbing Self-Diagnosis & Leak Resolution Modal -->
<ResidentLeakDiagnosticModal 
  bind:open={showDiagnosticModal}
  propertyId={portal?.property_id || 700}
  unitId={portal?.unit_id || ''}
  unitLabel={portal?.unit_label || unitName}
  onResolved={loadDashboard}
/>

<!-- Stripe & Plaid Secure Checkout Modal -->
<StripePaymentModal
  bind:open={showStripeModal}
  invoiceId={portal?.latest_invoice?.invoice_id || 'inv-demo-1'}
  propertyId={portal?.property_id || 700}
  amount={portal?.latest_invoice?.total_cost_usd || invoiceUnit.total_billed_cost_usd || 42.50}
  unitLabel={portal?.unit_label || unitName}
  tenantName={portal?.tenant_name || currentUser?.name || 'Resident'}
  invoiceNumber={portal?.latest_invoice?.invoice_number || 'INV-202609-01'}
  onPaid={loadDashboard}
/>

<style>
  :global(.btn-white) {
    background-color: white;
    color: black;
    border: none;
  }
</style>
